import { useEffect, useMemo, useState } from 'react';
import type {
  EmployerFootprintEmployerPinDto,
  EmployerFootprintPurchasePinDto,
} from '../../api/employerVerificationDashboardApi';
import {
  employerPlotAddress,
  employerPlotName,
  normalizeCanadaCoords,
  resolveEmployerLocation,
  type EmployerResolvedLocation,
} from '../../lib/employerResolvedLocation';
import {
  geocodeAddressCached,
  normalizeGeocodeAddressKey,
} from '../../lib/googleMapsGeocode';
import { buildGeocodeQueue } from '../../lib/googleMapsSessionCache';
import { configureGoogleMapsLoader, googleMapsApiKey } from '../../lib/googleMapsRuntime';

/** Cap unique geocode lookups so long debit lists do not flood Google. */
const MAX_UNIQUE_GEOCODES = 16;

function mapsApiKey(): string {
  return googleMapsApiKey();
}

function configureMapsLoader(): boolean {
  return configureGoogleMapsLoader();
}

function hasCoords(
  pin: { latitude: number | null; longitude: number | null } | null | undefined,
): pin is { latitude: number; longitude: number } {
  if (
    pin == null ||
    typeof pin.latitude !== 'number' ||
    typeof pin.longitude !== 'number' ||
    !Number.isFinite(pin.latitude) ||
    !Number.isFinite(pin.longitude)
  ) {
    return false;
  }
  return normalizeCanadaCoords(pin.latitude, pin.longitude) != null;
}

function resolvedServerCoords(
  pin: { latitude: number | null; longitude: number | null },
): { lat: number; lng: number } | null {
  if (typeof pin.latitude !== 'number' || typeof pin.longitude !== 'number') {
    return null;
  }
  return normalizeCanadaCoords(pin.latitude, pin.longitude);
}

export type ResolvedFootprintPurchasePin = EmployerFootprintPurchasePinDto & {
  latitude: number;
  longitude: number;
};

export type ResolvedFootprintEmployerPin = EmployerFootprintEmployerPinDto & {
  latitude: number;
  longitude: number;
};

export type FootprintGeocodeStatus = 'idle' | 'loading' | 'ready' | 'error';

export type FootprintMapPins = {
  purchases: ResolvedFootprintPurchasePin[];
  employer: ResolvedFootprintEmployerPin | null;
  /** Authoritative employer location (coords + source + mismatch + distance). */
  resolvedLocation: EmployerResolvedLocation | null;
  /** True when we should reserve map UI (pins exist or geocode in flight / possible). */
  showMap: boolean;
  geocoding: boolean;
  geocodeStatus: FootprintGeocodeStatus;
  geocodeError: string | null;
  /** Address geocode used because AI left lat/lng null or mismatch overrode server. */
  usedGeocodeFallback: boolean;
  /** Purchases that already had server coords (for “Purchases mapped” copy). */
  serverCoordPurchaseCount: number;
};

/**
 * Prefer the declared employer address for the map pin. AI `possibleEmployer` may carry
 * headquarters address or coords; geocode the declared address client-side when AI omits coords.
 */
export function useFootprintMapPins(input: {
  purchases: EmployerFootprintPurchasePinDto[];
  employer: EmployerFootprintEmployerPinDto | null;
  employerAddressFallback: string | null | undefined;
  employerNameFallback: string | null | undefined;
  purchaseAreaFallback: string | null | undefined;
}): FootprintMapPins {
  const employerAddressFallback = (input.employerAddressFallback ?? '').trim();
  const purchaseArea = (input.purchaseAreaFallback ?? '').trim();
  const employerNameFallback = (input.employerNameFallback ?? '').trim();

  const employerAddressForPlot = useMemo(
    () => employerPlotAddress(employerAddressFallback, input.employer?.address),
    [employerAddressFallback, input.employer?.address],
  );

  const employerNameForPlot = useMemo(
    () => employerPlotName(employerNameFallback, input.employer?.name),
    [employerNameFallback, input.employer?.name],
  );

  const serverCoordPurchaseCount = useMemo(
    () => input.purchases.filter((p) => hasCoords(p)).length,
    [input.purchases],
  );

  const readyPurchases = useMemo(() => {
    const out: ResolvedFootprintPurchasePin[] = [];
    for (const p of input.purchases) {
      const coords = resolvedServerCoords(p);
      if (!coords) continue;
      out.push({ ...p, latitude: coords.lat, longitude: coords.lng });
    }
    return out;
  }, [input.purchases]);

  const readyEmployer = useMemo(() => {
    if (!input.employer) return null;
    const coords = resolvedServerCoords(input.employer);
    if (!coords) return null;
    return {
      ...input.employer,
      latitude: coords.lat,
      longitude: coords.lng,
    } satisfies ResolvedFootprintEmployerPin;
  }, [input.employer]);

  const addressesToGeocode = useMemo(
    () =>
      buildGeocodeQueue({
        // Geocode the declared employer address first — not the AI HQ string on possibleEmployer.
        required: [
          employerAddressForPlot || null,
          readyPurchases.length === 0 ? purchaseArea : null,
        ],
        optional: [
          ...input.purchases.filter((p) => !hasCoords(p)).map((p) => p.address),
          // Only fall back to AI address when the application record has no declared address.
          !employerAddressFallback && input.employer?.address ? input.employer.address : null,
        ],
        limit: MAX_UNIQUE_GEOCODES,
      }),
    [
      input.purchases,
      input.employer,
      readyPurchases.length,
      employerAddressForPlot,
      employerAddressFallback,
      purchaseArea,
    ],
  );

  const needsGeocode = addressesToGeocode.length > 0 && Boolean(mapsApiKey());
  const canShowMapShell =
    readyPurchases.length > 0 ||
    Boolean(readyEmployer) ||
    needsGeocode ||
    Boolean(employerAddressForPlot) ||
    Boolean(purchaseArea) ||
    input.purchases.some((p) => Boolean(p.address?.trim()));

  const [geocodeByAddress, setGeocodeByAddress] = useState<
    Record<string, { lat: number; lng: number; locationType: string | null }>
  >({});
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeStatus, setGeocodeStatus] = useState<FootprintGeocodeStatus>('idle');
  const [geocodeError, setGeocodeError] = useState<string | null>(null);

  const geocodeKey = useMemo(() => JSON.stringify(addressesToGeocode), [addressesToGeocode]);

  const purchaseBias = useMemo(() => {
    if (readyPurchases.length === 0) return null;
    return {
      lat: readyPurchases.reduce((s, p) => s + p.latitude, 0) / readyPurchases.length,
      lng: readyPurchases.reduce((s, p) => s + p.longitude, 0) / readyPurchases.length,
    };
  }, [readyPurchases]);

  useEffect(() => {
    if (!needsGeocode) {
      setGeocodeByAddress({});
      setGeocoding(false);
      setGeocodeStatus('idle');
      setGeocodeError(null);
      return;
    }

    let cancelled = false;
    setGeocoding(true);
    setGeocodeStatus('loading');
    setGeocodeError(null);

    void (async () => {
      try {
        if (!configureMapsLoader()) {
          if (!cancelled) {
            setGeocodeByAddress({});
            setGeocoding(false);
            setGeocodeStatus('error');
            setGeocodeError('Google Maps API key is not configured.');
          }
          return;
        }

        let hardError: string | null = null;
        const entries = await Promise.all(
          addressesToGeocode.map(async (address) => {
            try {
              const result = await geocodeAddressCached({
                address,
                bias: purchaseBias ?? (readyEmployer
                  ? { lat: readyEmployer.latitude, lng: readyEmployer.longitude }
                  : null),
              });
              if (!result.ok) {
                if (result.hardFailure && !hardError) hardError = result.error;
                return null;
              }
              const top = result.candidates[0];
              if (!top) return null;
              return [
                normalizeGeocodeAddressKey(address),
                {
                  lat: top.location.lat,
                  lng: top.location.lng,
                  locationType: top.locationType ?? null,
                },
              ] as const;
            } catch (err) {
              if (!hardError) {
                hardError = err instanceof Error ? err.message : 'Geocode failed.';
              }
              return null;
            }
          }),
        );

        if (cancelled) return;
        const next: Record<string, { lat: number; lng: number; locationType: string | null }> = {};
        for (const entry of entries) {
          if (!entry) continue;
          next[entry[0]] = entry[1];
        }
        setGeocodeByAddress(next);
        if (Object.keys(next).length === 0 && hardError) {
          setGeocodeStatus('error');
          setGeocodeError(hardError);
        } else {
          setGeocodeStatus('ready');
          setGeocodeError(hardError);
        }
      } catch (err) {
        if (!cancelled) {
          setGeocodeByAddress({});
          setGeocodeStatus('error');
          setGeocodeError(err instanceof Error ? err.message : 'Geocode failed.');
        }
      } finally {
        if (!cancelled) setGeocoding(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [geocodeKey, needsGeocode, addressesToGeocode, purchaseBias, readyEmployer]);

  const resolvePurchaseCoords = (
    pin: { latitude: number | null; longitude: number | null; address: string | null },
    extraAddress?: string | null,
  ): { lat: number; lng: number } | null => {
    const server = resolvedServerCoords(pin);
    if (server) return server;
    for (const raw of [pin.address, extraAddress]) {
      const t = (raw ?? '').trim();
      if (!t) continue;
      const hit = geocodeByAddress[normalizeGeocodeAddressKey(t)];
      if (hit && normalizeCanadaCoords(hit.lat, hit.lng)) return hit;
    }
    return null;
  };

  const purchases: ResolvedFootprintPurchasePin[] = [];
  const seenPurchasePlot = new Set<string>();
  let usedGeocodeFallback = false;

  for (const p of input.purchases) {
    const coords = resolvePurchaseCoords(p);
    if (!coords) continue;
    if (!hasCoords(p)) usedGeocodeFallback = true;
    const plotKey = `${coords.lat.toFixed(5)},${coords.lng.toFixed(5)}:${p.merchantName}`;
    if (seenPurchasePlot.has(plotKey)) continue;
    seenPurchasePlot.add(plotKey);
    purchases.push({
      ...p,
      latitude: coords.lat,
      longitude: coords.lng,
    });
  }

  // Cluster-only fallback when no purchase street addresses geocoded.
  if (purchases.length === 0 && purchaseArea) {
    const hit = geocodeByAddress[normalizeGeocodeAddressKey(purchaseArea)];
    if (hit) {
      usedGeocodeFallback = true;
      purchases.push({
        merchantName: `Purchase area · ${purchaseArea}`,
        address: purchaseArea,
        latitude: hit.lat,
        longitude: hit.lng,
        category: 'geographic_cluster',
      });
    }
  }

  const employerGeocode =
    employerAddressForPlot
      ? geocodeByAddress[normalizeGeocodeAddressKey(employerAddressForPlot)] ?? null
      : null;

  const resolvedLocation = resolveEmployerLocation({
    serverLat: input.employer?.latitude,
    serverLng: input.employer?.longitude,
    geocoded: employerGeocode
      ? { lat: employerGeocode.lat, lng: employerGeocode.lng }
      : null,
    address: employerAddressForPlot || null,
    serverDistanceKm: input.employer?.distanceKm ?? null,
    purchasePoints: purchases.map((p) => ({ lat: p.latitude, lng: p.longitude })),
  });

  let employer: ResolvedFootprintEmployerPin | null = null;
  if (resolvedLocation && (employerAddressForPlot || input.employer)) {
    if (resolvedLocation.source === 'geocode') usedGeocodeFallback = true;
    employer = {
      name: employerNameForPlot || input.employer?.name || 'Possible employer',
      address: resolvedLocation.address || employerAddressForPlot || null,
      latitude: resolvedLocation.lat,
      longitude: resolvedLocation.lng,
      distanceKm: resolvedLocation.distanceKm,
    };
  }

  const hasResolvedPins = purchases.length > 0 || Boolean(employer);

  return {
    purchases,
    employer,
    resolvedLocation,
    showMap: canShowMapShell,
    geocoding: geocoding && !hasResolvedPins,
    geocodeStatus,
    geocodeError,
    usedGeocodeFallback,
    serverCoordPurchaseCount,
  };
}

export { normalizeCanadaCoords } from '../../lib/employerResolvedLocation';
